







































import { computed, defineComponent } from "@vue/composition-api";
import { ProfileLink, ProfileLinkType } from "@scrile/api-provider/dist/api/ProfileLinksProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useDarkModeController from "@/hooks/useDarkModeController";
import { profileLinksToMap } from "@/hooks/useProfileLinks";
import { darkModeReplacePostfix } from "@/lib/iconHelpers";
import { getQuestionaryInfoFields } from "@/lib/questionaryFieldsHelper";
import config from "@/config";
import BaseIcon from "@/components/BaseIcon";
import { linkFields } from "@/views/PageProfileEdit/const";

interface IconLink {
  id: string;
  href: string;
  iconName: string;
}
interface Info {
  id: string;
  text: string;
  class: string;
  tag: string;
  icon?: string;
  attrs: Record<string, string>;
}

export default defineComponent({
  name: "QuestionaryInfoPrinter",
  components: { BaseIcon },
  props: {
    questionaryInfo: { type: Object as () => Record<string, string>, required: true },
    user: { type: Object as () => UserPublicData, required: true },
    profileLinks: { type: Array as () => ProfileLink[], default: () => [] },
  },
  setup(props, { emit }) {
    const { isDarkMode } = useDarkModeController();
    const restrictedMember = computed(() => {
      return config.featureRestrictMember && checkRole(props.user.role, "member");
    });
    const availableInfos = computed((): Info[] => {
      return getQuestionaryInfoFields()
        .filter(
          i =>
            !i.fieldMeta?.amazon &&
            !i.fieldMeta?.calendar &&
            (restrictedMember.value ? i.fieldMeta?.allowedForMember : true) &&
            props.questionaryInfo[i.name]
        )
        .map(infoField => {
          const classes = [
            ...(infoField.fieldMeta?.description ? ["caption"] : []),
            ...(infoField.fieldMeta?.url ? ["questionary-info__link link mt-2"] : []),
          ];
          let text = props.questionaryInfo[infoField.name];
          if (infoField.fieldMeta?.url) {
            text = text.replace(/(^\w+:|^)\/\//, "");
          }
          return {
            id: infoField.name,
            text,
            class: classes.join(" "),
            tag: infoField.fieldMeta?.url ? "a" : "div",
            attrs: {
              ...(infoField.fieldMeta?.url ? { href: props.questionaryInfo[infoField.name], target: "_blank" } : {}),
            },
            ...(infoField.fieldMeta?.icon ? { icon: infoField.fieldMeta?.icon } : {}),
          };
        });
    });

    const availableLinks = computed(() => {
      const linksMap = profileLinksToMap(props.profileLinks);
      return linkFields.reduce((out, linkField) => {
        const link = linksMap[linkField.name as ProfileLinkType];
        if (link?.hasUrl) {
          out.push({
            id: link.id,
            iconName: linkField.appendIcon + "",
            tag: link.url ? "a" : "div",
            attrs: link.url
              ? {
                  href: `${linkField.fieldMeta?.site ?? ""}${link.url}`,
                  target: "_blank",
                }
              : {},
            listeners:
              !link.url && link.pricePackage
                ? {
                    click: () => emit("purchase:profileLink", link),
                  }
                : {},
          });
        }
        return out;
      }, [] as any[]);
    });

    const availableAmazonLinks = computed(() => {
      return getQuestionaryInfoFields()
        .filter(i => i.fieldMeta?.amazon)
        .reduce((out, amazonField) => {
          if (props.questionaryInfo[amazonField.name])
            out.push({
              id: amazonField.name,
              href: props.questionaryInfo[amazonField.name],
              iconName: darkModeReplacePostfix(amazonField.fieldMeta?.logo ?? "", isDarkMode.value),
            });
          return out;
        }, [] as IconLink[]);
    });
    return { isDarkMode, availableInfos, availableAmazonLinks, availableLinks };
  },
});
